import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { deleteFaqExample, updateFaqExample } from '../../../api/faq'
import { ReactComponent as PlusCircle } from '../../../assets/icons/add-circle.svg'
import { ReactComponent as CheckmarkCircle } from '../../../assets/icons/checkmark-circle.svg'
import { ReactComponent as Close } from '../../../assets/icons/close.svg'
import { ReactComponent as Pencil } from '../../../assets/icons/pencil.svg'
import { ReactComponent as Thrash } from '../../../assets/icons/trash.svg'
import { IFaqExamplesItem } from '../faqModels'

import NewExampleForm from './FaqNewExample/FaqNewExample'

import styles from './faq-example.module.scss'

interface IFaqExamplesSectionProps {
    data: IFaqExamplesItem[]
    selectedFaqItem: string
    getDataFromApi: () => void
    canDelete: boolean
}

function FaqExamplesSection(props: IFaqExamplesSectionProps) {
    const { data, selectedFaqItem, getDataFromApi, canDelete } = props

    const { t } = useTranslation()

    const [editingExampleText, setEditingExampleText] = useState<string>('')
    const [editingExampleId, setEditingExampleId] = useState<string | null>(
        null
    )
    const [addingExample, setAddingExample] = useState(false)

    const handleSaveEditedExample = async (exampleId: string) => {
        try {
            await updateFaqExample(
                selectedFaqItem,
                exampleId,
                editingExampleText
            )
            getDataFromApi()
            setEditingExampleId(null)
            setEditingExampleText('')
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const handleEditExample = (exampleId: string, text: string) => {
        setEditingExampleId(exampleId)
        setEditingExampleText(text)
    }

    const handleDeleteExample = async (exampleId: string) => {
        if (!canDelete) {
            return
        }

        const confirmDelete = confirm(t('delete_example_confirm'))
        if (!confirmDelete) return

        try {
            await deleteFaqExample(selectedFaqItem, exampleId)
            getDataFromApi()
        } catch (error) {
            console.error('ERROR:', error)
        }
    }

    const handleFormSubmit = async (e: FormEvent) => {
        e.preventDefault()
        if (!editingExampleId) {
            return
        }

        const confirmUpdate = confirm(t('save_example_confirm'))
        if (!confirmUpdate) {
            return
        }

        await handleSaveEditedExample(editingExampleId)
    }

    return (
        <div className={styles.faqExample}>
            <div className={styles.faqExample__header}>
                <h2>{t('questions')}</h2>

                <div>
                    {!addingExample && (
                        <button
                            className={styles.faqExample__btn}
                            onClick={() => setAddingExample(true)}
                        >
                            <PlusCircle />
                            {t('add_new')}
                        </button>
                    )}
                </div>
            </div>

            {addingExample && (
                <NewExampleForm
                    selectedFaqItem={selectedFaqItem}
                    getDataFromApi={getDataFromApi}
                    onCancel={() => setAddingExample(false)}
                />
            )}
            <div className={styles.faqExample__body}>
                {data &&
                    data.map((item, index) => (
                        <div key={index}>
                            {editingExampleId === item._id ? (
                                <FaqExampleEditForm
                                    onSubmit={handleFormSubmit}
                                    text={editingExampleText}
                                    setText={setEditingExampleText}
                                    onCancel={() => setEditingExampleId(null)}
                                />
                            ) : (
                                <FaqExampleView
                                    item={item}
                                    canDelete={canDelete}
                                    onEdit={() =>
                                        handleEditExample(item._id, item.text)
                                    }
                                    onDelete={() =>
                                        handleDeleteExample(item._id)
                                    }
                                />
                            )}
                        </div>
                    ))}
            </div>
        </div>
    )
}

interface IFaqExampleEditFormProps {
    onSubmit: (e: FormEvent) => void
    text: string
    setText: (text: string) => void
    onCancel: () => void
}

const FaqExampleEditForm = (props: IFaqExampleEditFormProps) => {
    const { onSubmit, text, setText, onCancel } = props

    const saveButtonDisabled = text.trim() === ''

    return (
        <form onSubmit={onSubmit}>
            <div className={styles.faqExample__row}>
                <textarea
                    className={styles.faqExample__row__input}
                    rows={6}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                />
                <div className={styles.faqExample__buttonsWrapper}>
                    <button
                        type="submit"
                        disabled={saveButtonDisabled}
                        className={`${styles.faqExample__borderBtn} ${
                            saveButtonDisabled
                                ? styles.faqExample__disabledBtn
                                : ''
                        }`}
                    >
                        <CheckmarkCircle />
                    </button>
                    <button
                        type="button"
                        className={styles.faqExample__borderBtn}
                        onClick={onCancel}
                    >
                        <Close />
                    </button>
                </div>
            </div>
        </form>
    )
}

interface IFaqExampleViewProps {
    item: IFaqExamplesItem
    onEdit: () => void
    onDelete: () => void
    canDelete: boolean
}

const FaqExampleView = (props: IFaqExampleViewProps) => {
    const { item, onEdit, onDelete, canDelete } = props

    return (
        <div className={styles.faqExample__row}>
            <div className={styles.faqExample__row__text}>{item.text}</div>
            <div className={styles.faqExample__row__buttons}>
                <button
                    className={styles.faqExample__borderBtn}
                    onClick={onEdit}
                >
                    <Pencil />
                </button>
                {canDelete && (
                    <button
                        className={styles.faqExample__borderBtn}
                        onClick={onDelete}
                    >
                        <Thrash />
                    </button>
                )}
            </div>
        </div>
    )
}

export default FaqExamplesSection
